"use client";
import React from "react";
import {
  CalendarIcon,
  TicketIcon,
  CircleCheckIcon,
  CircleXIcon,
  ArchiveIcon,
} from "lucide-react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";
import { Ticket } from "@dripit/types";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Skeleton,
  Checkbox,
} from "@/components/ui";
import { formatTimestamp, usdcToUsd } from "@/helpers";
import isEmpty from "lodash.isempty";
import { cn } from "@/helpers/tailwind";
import { TicketsTableColumns, TicketsColumnKey } from "./TicketsTableColumns";
import dayjs from "dayjs";

interface TicketsTableProps {
  className?: string;
  data: Ticket[];
  columnsToInclude?: TicketsColumnKey[];
  loading: boolean;
  onSelectRow?: (ticketIds: Ticket["tokenId"][]) => void;
}

export const TicketsTable: React.FC<TicketsTableProps> = ({
  className,
  data,
  columnsToInclude = [],
  loading,
  onSelectRow,
}) => {
  const enableSelection = onSelectRow !== undefined;
  const [selectedIds, setSelectedIds] = React.useState<Ticket["tokenId"][]>([]);

  const columns = React.useMemo(() => {
    const baseColumns = isEmpty(columnsToInclude)
      ? TicketsTableColumns
      : TicketsTableColumns.filter((column) =>
          columnsToInclude.includes(column.accessorKey as TicketsColumnKey)
        );

    if (enableSelection) {
      return [
        {
          id: "select",
          header: () => null,
          cell: ({ row }: { row: any }) => {
            const ticket = row.original;
            return (
              <Checkbox
                checked={selectedIds.includes(ticket.tokenId)}
                onCheckedChange={() => {
                  const newSelectedIds = selectedIds.includes(ticket.tokenId)
                    ? selectedIds.filter((id) => id !== ticket.tokenId)
                    : [...selectedIds, ticket.tokenId];
                  setSelectedIds(newSelectedIds);
                  onSelectRow?.(newSelectedIds);
                }}
                aria-label="Select row"
                className="border-slate-500 data-[state=checked]:border-teal-400 data-[state=checked]:bg-teal-400"
              />
            );
          },
        },
        ...baseColumns,
      ];
    }
    return baseColumns;
  }, [columnsToInclude, enableSelection, selectedIds, onSelectRow]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  const getContent = () => {
    if (loading) {
      return [0, 1, 2].map((i) => (
        <TableRow key={i} className="bg-transparent">
          {[...Array(columns.length).keys()].map((j) => (
            <TableCell key={j} colSpan={1} className="h-9 p-3">
              <Skeleton className="h-5 w-full bg-slate-600/80" />
            </TableCell>
          ))}
        </TableRow>
      ));
    }
    if (isEmpty(table.getRowModel().rows)) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} className="py-7">
            <div className="flex flex-col items-center justify-center">
              <TicketIcon />
              <h1>No tickets at the moment</h1>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    return table.getRowModel().rows.map((row) => {
      const ticket = row.original;
      return (
        <TableRow
          key={row.id}
          data-state={selectedIds.includes(ticket.tokenId) && "selected"}
          className={cn(
            "bg-transparent text-sm",
            enableSelection && "cursor-pointer hover:bg-slate-800"
          )}
          onClick={() => {
            if (enableSelection) {
              const newSelectedIds = selectedIds.includes(ticket.tokenId)
                ? selectedIds.filter((id) => id !== ticket.tokenId)
                : [...selectedIds, ticket.tokenId];
              setSelectedIds(newSelectedIds);
              onSelectRow?.(newSelectedIds);
            }
          }}>
          {row.getVisibleCells().map((cell) => {
            const cellValue = cell.getValue();
            const columnId = cell.column.id;

            let cellContent;
            if (columnId === TicketsColumnKey.Amount) {
              cellContent = <span>$ {usdcToUsd(Number(cellValue) ?? 0)}</span>;
            } else if (
              [
                TicketsColumnKey.MintedAt,
                TicketsColumnKey.ExpirationDate,
              ].includes(columnId as TicketsColumnKey)
            ) {
              cellContent = (
                <span>
                  {/* <CalendarIcon className="mr-2 inline size-4" /> */}
                  <span>
                    {formatTimestamp(
                      Number(cellValue) ?? 0,
                      "DD MMM YY, HH:mm"
                    )}
                  </span>
                </span>
              );
            } else if (columnId === "status") {
              let statusColor = "text-green-400";
              let icon = <CircleCheckIcon className="size-4" />;
              let content = "Active";

              if (ticket.used) {
                statusColor = "text-slate-400";
                icon = <ArchiveIcon className="size-4" />;
                content = "Used";
                // } else if (new Date() > new Date(ticket.expiration_date * 1000)) {
              } else if (
                dayjs().isAfter(dayjs((ticket.expiration_date ?? 0) * 1000))
              ) {
                statusColor = "text-red-400";
                icon = <CircleXIcon className="size-4" />;
                content = "Expired";
              }

              cellContent = (
                <span className={cn(statusColor, "flex items-center gap-2")}>
                  {icon}
                  {content}
                </span>
              );
            } else {
              cellContent = flexRender(
                cell.column.columnDef.cell,
                cell.getContext()
              );
            }
            return <TableCell key={cell.id}>{cellContent}</TableCell>;
          })}
        </TableRow>
      );
    });
  };

  return (
    <div className={cn("w-full overflow-x-auto rounded-xl border", className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="bg-transparent text-slate-400">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{getContent()}</TableBody>
      </Table>
    </div>
  );
};
